import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle, MailCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const heading = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState('verifying'); // verifying | success | error
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token) { setState('error'); setMessage('No verification token provided.'); return; }
    (async () => {
      try {
        const res = await axios.post(`${API_URL}/api/auth/unified/verify-email`, { token });
        setState('success');
        setMessage(res.data?.message || 'Your email has been verified.');
      } catch (err) {
        setState('error');
        setMessage(err.response?.data?.detail || 'This verification link is invalid or has expired.');
      }
    })();
  }, [token]);

  const resend = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/unified/resend-verification`, { email: resendEmail, origin_url: window.location.origin });
    } catch { /* generic */ }
    setResent(true);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="verify-email-page">
      <LandingNavBar />
      <div className="flex-1 flex items-center justify-center p-6 pt-28">
        <div className="w-full max-w-md text-center">
          {state === 'verifying' && (
            <>
              <div className="mx-auto w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-6">
                <Loader2 className="w-8 h-8 text-cyan-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={heading}>Verifying your email…</h2>
              <p className="text-slate-500">Hang tight, this only takes a second.</p>
            </>
          )}

          {state === 'success' && (
            <>
              <div className="mx-auto w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={heading} data-testid="verify-success">Email verified!</h2>
              <p className="text-slate-500 mb-8 leading-relaxed">{message}</p>
              <Link to="/auth/login">
                <Button className="w-full h-12 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="verify-goto-login">
                  Go to login
                </Button>
              </Link>
            </>
          )}

          {state === 'error' && (
            <>
              <div className="mx-auto w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-6">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={heading} data-testid="verify-error">Verification failed</h2>
              <p className="text-slate-500 mb-6 leading-relaxed">{message}</p>
              {resent ? (
                <div className="flex items-center justify-center gap-2 text-emerald-600 font-semibold" data-testid="verify-resent">
                  <MailCheck className="w-5 h-5" /> A new verification link has been sent.
                </div>
              ) : (
                <div className="space-y-3">
                  <Input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="Enter your email to get a new link"
                    className="h-12 rounded-xl"
                    data-testid="verify-resend-email"
                  />
                  <Button
                    onClick={resend}
                    disabled={!resendEmail}
                    className="w-full h-12 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-[#0B1120] font-semibold"
                    data-testid="verify-resend-btn"
                  >
                    Resend verification email
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <LandingFooter />
    </div>
  );
}
